import {doppeActionTypeDefineMetadata} from '../../client-server-common/doppe-action-types/doppe-action-type';
import type {DoppeHideableValue} from '../../client-server-common';
import {
    BymoPagePropsContentSettingSemanticMeaning,
    BymoPagePropsContentSettingType,
    bymoPagePropsCreateContentSettingMetadata,
    BymoPagePropsSpecialContentSettingMetadata,
    DoppeActionWidgetOpeningMode,
    doppeHideableValueCreateHidden,
    doppeHideableValueGetValue,
    doppeHideableValueIsVisibleAndNotEqualValue
} from '../../client-server-common';
import type {WixMediaResource} from '@wix/devzai-common-wix';
import {wixMediaResourceReplaceExternalImage} from '@wix/devzai-common-wix';
import {asArray, immerProduce, RichTextContent} from '@wix/devzai-utils-common';
import {
    doppeDtoActionSettingsRemoveInnerCtaContentForLockedAction
} from '../../client-server-common/types/doppe-dto-action';
import type {DoppeActionCtaButtonSettings} from '../../client-server-common/types/doppe-action-cta-button-settings';
import {
    doppeActionCtaButtonTypeGetDefaultCtaButtonSettingsMigration,
    getDefaultDoppeActionCtaButtonSettings
} from '../../client-server-common/types/doppe-action-cta-button-settings';
import type {DoppeLinkSpec} from '../../client-server-common/types/doppe-action-cta-button';
import {
    doppeActionCtaButtonsResetSettingsForLock,
    doppeLinkSpecCreateDefault,
    doppeLinkSpecResolveBusinessEntityReference
} from '../../client-server-common/types/doppe-action-cta-button';
import {arrayRemoveNullValues} from '@wix/devzai-utils-common';
import {
    doppeActionCtaButtonsResolveMainURL,
    doppeLinkSpecResolveToUrl
} from '../doppe-actions-common/doppe-cta-button-utils/doppe-cta-button-utils';
import type {
    DoppeActionContentSectionsSettings
} from '../../client-server-common/types/doppe-action-content-sections-settings';
import {
    doppeActionContentSectionSettingsDefaultValuesMigration
} from '../../client-server-common/types/doppe-action-content-sections-settings';
import type {DoppeActionCtaButtonsSettings} from '../../client-server-common/types/doppe-action-cta-buttons-settings';
import {
    doppeActionCtaButtonsSettingsDefaultValuesMigration
} from '../../client-server-common/types/doppe-action-cta-buttons-settings';


export namespace DoppeActionTypeLinkView {

    export interface LinkSettings  {
        url: DoppeLinkSpec;
        title: DoppeHideableValue<string>;
        description: DoppeHideableValue<RichTextContent>;
        image: WixMediaResource[] | null;
        couponCode: DoppeHideableValue<string>;
        ribbon: DoppeHideableValue<string>;
    }

    export interface ActionSettings extends
        DoppeActionContentSectionsSettings,
        DoppeActionCtaButtonsSettings,
        DoppeActionTypeLinkView.LinkSettings {

    }

    export type DeprecatedFields = Partial<DoppeActionCtaButtonSettings> & {
        linkButtonText?: DoppeHideableValue<string>;
    }

    export type PartialProps = Partial<ActionSettings> & DeprecatedFields;
}

export const doppeActionTypeLinkView = doppeActionTypeDefineMetadata<DoppeActionTypeLinkView.ActionSettings, DoppeActionTypeLinkView.PartialProps>({
    id: 'io.bymo.action/link-view',
    resolveActionSettings: action => {

        const {
            url,
            linkButtonText = 'Continue',
            ctaButton,
            ctaButtons,
            sections,
            ...restSettings
        } = action.settings;

        // Migrate url: support both old string format and new DoppeLinkSpec
        const resolvedUrl: DoppeLinkSpec = url !== undefined 
            ? (typeof url === 'string' ? url : url) 
            : doppeLinkSpecCreateDefault();

        return {
            url: resolvedUrl,
            title: 'Link title',
            description: doppeHideableValueCreateHidden('Link description'),
            image: null,
            linkButtonText: linkButtonText,
            couponCode: doppeHideableValueCreateHidden('Coupon code'),
            ribbon: doppeHideableValueCreateHidden('SALE'),
            ...getDefaultDoppeActionCtaButtonSettings({
                buttonText: linkButtonText,
                showButton: true,
            }),
            ...doppeActionCtaButtonTypeGetDefaultCtaButtonSettingsMigration({
                hasSourceURL: true,
                ctaButton: ctaButton,
            }),
            ...doppeActionContentSectionSettingsDefaultValuesMigration(action.settings),
            ...doppeActionCtaButtonsSettingsDefaultValuesMigration(action.settings, true),
            ...restSettings
        }
    },
    isLinkOpener: true,
    actionSettingsMetadata: {
        contentSettingsMetadata: {
            // DoppeLinkSpec is a union type, mark as ToBeConsidered for AI metadata
            url: BymoPagePropsSpecialContentSettingMetadata.ToBeConsidered,
            title: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.Text, BymoPagePropsContentSettingSemanticMeaning.EntityTitle),
            description: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.RichText, BymoPagePropsContentSettingSemanticMeaning.EntityDescription),
            image: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.MultipleMedia, BymoPagePropsContentSettingSemanticMeaning.EntityMedia),
            couponCode: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.Text, BymoPagePropsContentSettingSemanticMeaning.EntityCouponCode),
            ribbon: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.Text, BymoPagePropsContentSettingSemanticMeaning.EntityRibbon)
        },
        supportsActionCtaButtons: {
            customCtaButtonTypes: {}
        },
        supportsContentSections: {
            customContentSectionsTypes: {}
        },
        hideableContentSettingsNames: {
                couponCode: true,
                description: true,
                ribbon: true,
                title: true
            }
    },
    actionListItemSettingsMetadata: false,
    isWidgetOpener: true,
    canRenderInPage: true,
    skipReportingOnGoalAchievedOnActionButtonClick: (actionContext) => actionContext.action.widgetOpeningMode === DoppeActionWidgetOpeningMode.OpenCTA ? false : true,
    resolveUsedMedia: actionSettings => {
        return actionSettings.image ? asArray(actionSettings.image) : [];
    },
    resolveMainMedia: actionSettings => {
        return actionSettings.image ? asArray(actionSettings.image)[0] : null
    },
    resolveCouponData: actionSettings => {
        return {
            showCouponCode: doppeHideableValueIsVisibleAndNotEqualValue(actionSettings.couponCode, ''),
            couponCode: doppeHideableValueGetValue(actionSettings.couponCode),
            couponDescription: doppeHideableValueIsVisibleAndNotEqualValue(actionSettings.description, '') ? actionSettings.description : '',
            couponTitle: doppeHideableValueIsVisibleAndNotEqualValue(actionSettings.title, '') ? actionSettings.title : '',
        }
    },
    replaceExternalImages: (actionSettings, externalImagesUrlToImageResourceMap) => {
        return immerProduce(actionSettings, actionSettings => {
            if (actionSettings.image) {
                actionSettings.image = asArray(actionSettings.image).map(
                    image => wixMediaResourceReplaceExternalImage(image, externalImagesUrlToImageResourceMap))
            }
        })
    },
    resolveMainLink: (actionSettings, viewerLang, bymoPageProps, viewerPagesRoutesManager, bymoPageContextProps) => {
        // Use the common function to resolve DoppeLinkSpec to URL
        // Then also check CTA buttons for additional URL resolution
        return doppeActionCtaButtonsResolveMainURL(actionSettings, {
            sourceLinkSpec: actionSettings.url ?? null,
            viewerLang: viewerLang,
            bymoPageProps: bymoPageProps,
            viewerPagesRoutesManager: viewerPagesRoutesManager,
            bymoPageContextProps: bymoPageContextProps
        }) ?? doppeLinkSpecResolveToUrl(actionSettings.url, {
            bymoPageProps: bymoPageProps,
            viewerPagesRoutesManager: viewerPagesRoutesManager,
            bymoPageContextProps: bymoPageContextProps,
            viewerLang: viewerLang
        }) ?? null;
    },
    prepareForViewer: (actionSettings, options) => {
        doppeDtoActionSettingsRemoveInnerCtaContentForLockedAction(actionSettings, actionSettings => {
            // Reset url to empty string (valid DoppeLinkSpec)
            actionSettings.url = '';
            doppeActionCtaButtonsResetSettingsForLock(actionSettings);
        }, options);
    },
    resolveUsedBusinessEntities: actionSettings => {
        return arrayRemoveNullValues([
            doppeLinkSpecResolveBusinessEntityReference(actionSettings.url)
        ]);
    },
})